// @ts-check

/**
 * @file Renders one timelog dashboard log card.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import gettextConfig from "gettext-universal/build/src/config.js"
import {memo} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"

import Button from "../../../components/button"
import buildRoutePath from "../../../shared/build-route-path"
import Card from "../../../components/card"
import Link from "../../../components/link"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"
import {timelogComment, timelogDateString, timelogId, timelogInvoiced, timelogPricePerHourCents, timelogPricePerHourCurrency, timelogProjectId, timelogProjectName, timelogTaskName, timelogTimeSeconds, timelogUserEmail, timelogUserFirstName, timelogUserId, timelogUserLastName} from "./log-model"

/** @import Timelog from "../../../frontend-models/timelog" */
/** @import {ProjectedTimelogDashboardRow} from "../../../../../shared/projected-timelog-reporting-types.js" */
/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TimelogsDashboardLogCardProps
 * @property {InstanceType<typeof Timelog> | ProjectedTimelogDashboardRow} log Timelog to render.
 * @property {(timelogId: string) => unknown} onDeletePress Delete callback.
 * @property {(timelogId: string) => unknown} onEditPress Edit callback.
 * @property {Record<string, string | string[] | undefined>} params Current route params (forwarded so chip links keep system-test query params).
 */

/** @typedef {Record<string, never>} TimelogsDashboardLogCardState */

/**
 * Displays one timelog summary card.
 * @augments {ShapeComponent<TimelogsDashboardLogCardProps, TimelogsDashboardLogCardState>}
 */
class TimelogsDashboardLogCard extends ShapeComponent {
  static propTypes = PropTypesExact({
    log: PropTypes.object.isRequired,
    onDeletePress: PropTypes.func.isRequired,
    onEditPress: PropTypes.func.isRequired,
    params: PropTypes.object.isRequired
  })

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /**
   * Subscribes the card to locale updates and wires the theme palette.
   * @returns {void} No return value.
   */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the log card content.
   * @returns {ReactNode} Log card content.
   */
  render() {
    const {log, params} = this.p
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const comment = timelogComment(log)
    const logId = timelogId(log)
    const pricePerHourCents = timelogPricePerHourCents(log)
    const pricePerHourCurrency = timelogPricePerHourCurrency(log)
    const projectId = timelogProjectId(log)
    const projectName = timelogProjectName(log)
    const taskName = timelogTaskName(log)
    const timeSeconds = timelogTimeSeconds(log)
    const userId = timelogUserId(log)
    const canMutate = !timelogInvoiced(log)
    return (
      <Card
        style={styles.cardSpacing ||= {
          gap: 14,
          padding: 16
        }}
        testID={`timelogsDashboardScreen/log/${logId}`}
      >
        <View
          style={styles.header ||= {
            gap: 10
          }}
          testID={`timelogsDashboardScreen/log/${logId}/header`}
        >
          <View
            style={styles.headerTopRow ||= {
              alignItems: "flex-start",
              flexDirection: "row",
              justifyContent: "space-between"
            }}
            testID={`timelogsDashboardScreen/log/${logId}/headerTopRow`}
          >
            <View
              style={styles.headerText ||= {
                flex: 1,
                gap: 6,
                paddingRight: 12
              }}
              testID={`timelogsDashboardScreen/log/${logId}/headerText`}
            >
              <Link
                href={buildRoutePath(params, `/timelogs/${logId}`)}
                style={styles[`title-${themeName}`] ||= {
                  color: colors.link,
                  fontSize: 16,
                  fontWeight: 700
                }}
                testID={`timelogsDashboardScreen/log/${logId}/title`}
              >
                {taskName || comment || _("Untitled entry")}
              </Link>
              <Text
                style={styles[`meta-${themeName}`] ||= {
                  color: colors.textMuted,
                  fontSize: 13,
                  fontWeight: 600
                }}
                testID={`timelogsDashboardScreen/log/${logId}/meta`}
              >
                {_("%{date} · %{user}", {
                  date: formatDate(timelogDateString(log)),
                  user: userName(log, _)
                })}
              </Text>
              {comment.length > 0 &&
                <Text
                  style={styles[`comment-${themeName}`] ||= {
                    color: colors.textMuted,
                    fontSize: 13,
                    fontWeight: 500
                  }}
                  testID={`timelogsDashboardScreen/log/${logId}/comment`}
                >
                  {comment}
                </Text>
              }
            </View>
            {canMutate &&
              <View
                style={styles.actions ||= {
                  alignItems: "stretch",
                  gap: 8
                }}
                testID={`timelogsDashboardScreen/log/${logId}/actions`}
              >
                <Button
                  iconName="pen-to-square"
                  label={_("Edit")}
                  onPress={this.tt.onEditPress}
                  testID={`timelogsDashboardScreen/log/${logId}/editButton`}
                  variant="outline"
                />
                <Button
                  iconName="trash"
                  label={_("Delete")}
                  onPress={this.tt.onDeletePress}
                  testID={`timelogsDashboardScreen/log/${logId}/deleteButton`}
                  variant="destructive-outline"
                />
              </View>
            }
          </View>
        </View>
        <View
          style={styles.metricsRow ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 12
          }}
          testID={`timelogsDashboardScreen/log/${logId}/metrics`}
        >
          <MetricCardComponent
            label={_("Hours")}
            testID={`timelogsDashboardScreen/log/${logId}/hours`}
            value={formatHours(timeSeconds / 3600)}
          />
          <MetricCardComponent
            label={_("Rate")}
            testID={`timelogsDashboardScreen/log/${logId}/rate`}
            value={_("%{amount} %{currency}/h", {
              amount: formatInteger(pricePerHourCents / 100),
              currency: pricePerHourCurrency
            })}
          />
          <MetricCardComponent
            label={_("Amount")}
            testID={`timelogsDashboardScreen/log/${logId}/amount`}
            value={_("%{amount} %{currency}", {
              amount: formatInteger(
                (pricePerHourCents / 100) * (timeSeconds / 3600)
              ),
              currency: pricePerHourCurrency
            })}
          />
          <MetricCardComponent
            label={_("Status")}
            testID={`timelogsDashboardScreen/log/${logId}/status`}
            value={timelogInvoiced(log) ? _("Invoiced") : _("Pending")}
          />
        </View>
        <View
          style={styles.footer ||= {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 8
          }}
          testID={`timelogsDashboardScreen/log/${logId}/footer`}
        >
          {projectId ?
            <Link
              href={buildRoutePath(params, `/projects/${projectId}`)}
              style={styles[`footerChip-${themeName}`] ||= {
                backgroundColor: colors.infoBackground,
                borderRadius: 999,
                color: colors.link,
                fontSize: 12,
                fontWeight: 600,
                paddingHorizontal: 10,
                paddingVertical: 6
              }}
              testID={`timelogsDashboardScreen/log/${logId}/project`}
            >
              {projectName || _("Internal")}
            </Link>
            :
            <Text
              style={styles[`footerChip-${themeName}`]}
              testID={`timelogsDashboardScreen/log/${logId}/project`}
            >
              {projectName || _("Internal")}
            </Text>
          }
          {userId ?
            <Link
              href={buildRoutePath(params, `/users/${userId}`)}
              style={styles[`footerChip-${themeName}`]}
              testID={`timelogsDashboardScreen/log/${logId}/user`}
            >
              {userName(log, _)}
            </Link>
            :
            <Text
              style={styles[`footerChip-${themeName}`]}
              testID={`timelogsDashboardScreen/log/${logId}/user`}
            >
              {userName(log, _)}
            </Text>
          }
        </View>
      </Card>
    )
  }

  /**
   * Requests editing for this log.
   * @returns {void} No return value.
   */
  onEditPress = () => {
    this.p.onEditPress(timelogId(this.p.log))
  }

  /**
   * Requests deletion for this log.
   * @returns {void} No return value.
   */
  onDeletePress = () => {
    this.p.onDeletePress(timelogId(this.p.log))
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardLogCardProps>} */
const TimelogsDashboardLogCardComponent = memo(shapeComponent(TimelogsDashboardLogCard))

export default TimelogsDashboardLogCardComponent

/**
 * @typedef {object} MetricCardProps
 * @property {string} label Metric label.
 * @property {string} testID Base test id for the metric.
 * @property {string} value Metric value text.
 */

/** @typedef {Record<string, never>} MetricCardState */

/**
 * Displays one timelog metric value.
 * @augments {ShapeComponent<MetricCardProps, MetricCardState>}
 */
class MetricCard extends ShapeComponent {
  static propTypes = PropTypesExact({
    label: PropTypes.string.isRequired,
    testID: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires the active theme palette into the instance. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the metric card.
   * @returns {ReactNode} Metric card content.
   */
  render() {
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles[`metricCard-${themeName}`] ||= {
          backgroundColor: colors.surfaceMuted,
          borderRadius: 12,
          gap: 4,
          minWidth: 132,
          paddingHorizontal: 12,
          paddingVertical: 10
        }}
        testID={this.p.testID}
      >
        <Text
          style={styles[`metricLabel-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 12,
            fontWeight: 600
          }}
          testID={`${this.p.testID}/label`}
        >
          {this.p.label}
        </Text>
        <Text
          style={styles[`metricValue-${themeName}`] ||= {
            fontSize: 14,
            fontWeight: 700
          }}
          testID={`${this.p.testID}/value`}
        >
          {this.p.value}
        </Text>
      </View>
    )
  }
}

/** @type {NamedExoticComponent<MetricCardProps>} */
const MetricCardComponent = memo(shapeComponent(MetricCard))

/**
 * Builds the display name for a timelog user.
 * @param {InstanceType<typeof Timelog> | ProjectedTimelogDashboardRow} log Timelog containing user fields.
 * @param {(text: string, params?: Record<string, unknown>) => string} _ Function used to translate fallback copy.
 * @returns {string} User display name.
 */
function userName(log, _) {
  const fullName = [timelogUserFirstName(log), timelogUserLastName(log)]
    .filter(Boolean)
    .join(" ")
    .trim()
  return fullName || timelogUserEmail(log) || _("Unknown user")
}

/**
 * Formats a stored date for display.
 * @param {string | null} value Stored date value.
 * @returns {string} Localized date text.
 */
function formatDate(value) {
  if (!value) return ""
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return value
  return new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  ).toLocaleDateString()
}

/**
 * Formats decimal hours for display.
 * @param {number} value Hour count.
 * @returns {string} Formatted hours label.
 */
function formatHours(value) {
  return new Intl.NumberFormat(gettextConfig.getLocale() || undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1
  }).format(value) + " " + _("h")
}

/**
 * Formats an integer amount for display.
 * @param {number} value Numeric amount.
 * @returns {string} Formatted integer.
 */
function formatInteger(value) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0
  }).format(value)
}
